<script lang="ts">
  import type {
    GroupedTransaction,
    InvoiceRequestDetails,
    TransactionType,
    ResolvedTransactionDetails,
  } from "../../../api/transactions";
  import { INVOICE_TYPES, PAIDT_TYPES } from "../../../helpers/constants";
  import { currencyFormatter } from "../../../helpers/localise";
  import { t } from "../../../helpers/translations";
  import { timeAgo } from "../../../helpers/utils";
  import { linkTransactionsModal } from "../../../stores/link-transactions-modal-store";
  import Stack from "../../Stack.svelte";
  import TextNew from "../../TextNew.svelte";
  import Icon from "../../icons/Icon.svelte";
  import { resolvedTransactionTitle } from "./data";

  export let transactions: GroupedTransaction[] = [];
  export let requestInvoiceDetails: InvoiceRequestDetails | undefined;
  export let resolvedTransactionDetails: ResolvedTransactionDetails | undefined;
  export let onOpenTransaction: (transaction: GroupedTransaction) => void;
  export let onCloseTransaction: () => void;

  $: openTransaction = $linkTransactionsModal.secondaryTransaction;
  $: hasInvoiceTransaction = transactions.some((t) => INVOICE_TYPES.includes(t.type));

  const TYPE_DISPLAY_MAP: Record<string, string> = {
    order: "order",
    checkin: "issue",
    credit_request: "issue",
    creditnote: "credit note",
    refund: "credit note",
    invoice_arrived: "invoice",
    manual_invoice: "invoice",
    pro_invoice_arrived: "invoice",
    payment: "invoice",
  };

  function getTitle(transaction: GroupedTransaction) {
    if (transaction.type === "order") {
      return `order ${transaction.po_number} - ${transaction.line_item_count} items`;
    }

    if (INVOICE_TYPES.includes(transaction.type) || PAIDT_TYPES.includes(transaction.type)) {
      return `invoice - ${transaction.line_item_count} items, ${$currencyFormatter.format(
        transaction.amount_cents / 100
      )}`;
    }
    return TYPE_DISPLAY_MAP[transaction.type];
  }

  function getSubtitle(transaction: GroupedTransaction) {
    return transaction.confirmed_by ? `by ${transaction.confirmed_by}` : transaction.supplier_name;
  }

  function isResolved(
    resolvedTransactionDetails: ResolvedTransactionDetails | undefined
  ): resolvedTransactionDetails is Omit<ResolvedTransactionDetails, "type"> & {
    type: TransactionType;
  } {
    return !!resolvedTransactionDetails?.resolved;
  }
</script>

<div class="container">
  {#if requestInvoiceDetails && !hasInvoiceTransaction}
    <div class="transaction">
      <Stack vertical>
        <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
          >{t("transactions.invoiceRequested.title")}</TextNew
        >
        <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
          >{`${timeAgo(new Date(requestInvoiceDetails.request_date))} ${t(
            "global.actionedBy.name",
            0,
            { user: requestInvoiceDetails.request_user }
          )} `}</TextNew
        >
      </Stack>
    </div>
  {/if}
  {#if isResolved(resolvedTransactionDetails)}
    <div class="transaction">
      <Stack vertical>
        <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
          >{resolvedTransactionTitle(resolvedTransactionDetails.type)}</TextNew
        >
        <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
          >{t("global.actionedBy.name", 0, {
            user: resolvedTransactionDetails.resolved_by ?? "",
          })}</TextNew
        >
      </Stack>
    </div>
  {/if}
  {#each transactions as transaction, index}
    {@const isOpen = transaction.key === openTransaction?.key}
    <button
      class="transaction"
      class:border-bottom={index !== transactions.length - 1}
      on:click={isOpen ? onCloseTransaction : () => onOpenTransaction(transaction)}
    >
      <Stack vertical>
        <TextNew size="s">{getTitle(transaction)}</TextNew>
        <TextNew size="s" --rk-text-color="var(--rk-text-transparency-dark)"
          >{getSubtitle(transaction)}</TextNew
        >
      </Stack>
      <div class="icon" class:visible={isOpen}>
        <Icon
          name={isOpen ? "close" : "arrowOpen"}
          --w="32px"
          --h="32px"
          --color={!isOpen && "var(--rk-neutral-4)"}
          --stroke={isOpen && "var(--rk-neutral-4)"}
        />
      </div>
    </button>
  {/each}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
  }

  .transaction {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 12px 0;
  }

  .border-bottom {
    border-bottom: solid 1px var(--rk-divider-0);
  }

  .icon {
    display: none;
  }

  .icon.visible,
  .transaction:hover .icon {
    display: block;
  }
</style>
